import React, { useEffect, useState, useCallback } from 'react';
import * as api from '../../api';
import { RNLLoader, Modal, Btn, Input } from '../UI';
import MRRequestTable from './MRRequestTable';
import MRDetailsView from './MRDetailsView';
import MREditPage from './MREditPage';

// ─── Action modal ─────────────────────────────────────────────────────────────
function ActionModal({ modal, onClose, onConfirm, onUpdateModal }) {
  if (!modal) return null;
  const { actionType, row, unreviewedCount = 0 } = modal;
  const isForward = actionType === 'forward';
  const isReturn  = actionType === 'return';
  const stepName  = modal.nextStepInfo?.step_name;
  const title     = isForward
    ? (stepName ? `Forward to ${stepName}` : 'Forward Request')
    : isReturn ? 'Return Request' : 'Reject Request';
  const btnLabel  = isForward ? 'Confirm Forward' : isReturn ? 'Confirm Return' : 'Confirm Reject';
  const btnColor  = isForward ? '#16a34a' : isReturn ? '#f97316' : '#ef4444';
  const canSubmit = isForward ? true : !!(modal.comment || '').trim();

  return (
    <Modal title={title} onClose={onClose} width={520}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, fontSize: 13.5, lineHeight: 1.6 }}>

        {/* Intro sentence */}
        <p style={{ margin: 0 }}>
          You are about to <strong>{isForward ? 'forward' : isReturn ? 'return' : 'reject'}</strong> this{' '}
          {isForward
            ? `request${stepName ? ` to ${stepName}` : ''}`
            : isReturn ? 'purchase request'
            : 'purchase request. This is final — the request will not be resubmittable'}.
          {row?.request_ref && <> <strong>{row.request_ref}</strong>.</>}
        </p>

        {/* Amber warning for un-reviewed lines */}
        {unreviewedCount > 0 && (
          <div style={{
            background: '#fefce8', border: '1px solid #fde68a',
            borderRadius: 8, padding: '10px 14px',
            display: 'flex', gap: 10, alignItems: 'flex-start',
          }}>
            <span style={{ fontSize: 15, lineHeight: 1.3, flexShrink: 0 }}>⚠</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: 12.5, color: '#92400e' }}>
                {unreviewedCount} line item{unreviewedCount !== 1 ? 's' : ''} not individually reviewed
              </div>
              <div style={{ fontSize: 12, color: '#78350f', marginTop: 3 }}>
                {isReturn
                  ? 'You did not review the line items. You can still return this request without reviewing them.'
                  : <>All line items will automatically be marked <strong>{isForward ? 'APPROVED' : 'REJECTED'}</strong>, using your comment below as each line's {isForward ? 'approval' : 'rejection'} reason.</>
                }
              </div>
            </div>
          </div>
        )}

        {/* Comment */}
        <div>
          <label style={{ display: 'block', marginBottom: 8, fontSize: 13, fontWeight: 600 }}>
            Comment {!isForward && <span style={{ color: 'red' }}>*</span>}
          </label>
          <Input
            value={modal.comment || ''}
            onChange={v => onUpdateModal({ comment: v })}
            placeholder={
              isForward ? 'Optional note for the next stage…'
              : isReturn ? 'Explain why this request is being returned…'
              : 'Explain why this request is being rejected…'
            }
            as="textarea"
            rows={3}
          />
        </div>

        {/* Next Approval Step (forward only) */}
        {isForward && (() => {
          const nsi = modal.nextStepInfo;
          if (!nsi || nsi.approver_mode === 'is_final') return null;
          const label = nsi.approver_mode === 'returning_step'
            ? `Goes back to — ${nsi.step_name}`
            : `Next Approval Step — ${nsi.step_name}`;
          return (
            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 13, fontWeight: 600 }}>
                {label}
              </label>
              {nsi.approver_mode === 'specific_user' ? (
                <select
                  value={modal.nextApproverId || ''}
                  onChange={e => onUpdateModal({ nextApproverId: e.target.value })}
                  style={{
                    width: '100%', padding: '9px 12px', border: '1px solid var(--border)',
                    borderRadius: 8, fontSize: 13, background: 'var(--bg-card)', outline: 'none',
                    fontFamily: 'inherit', cursor: 'pointer', color: 'var(--text-primary)',
                  }}
                >
                  {(nsi.eligible_users || []).map(u => (
                    <option key={u.user_id || u.id} value={u.user_id || u.id}>
                      {u.name}{u.email ? ` (${u.email})` : ''}
                    </option>
                  ))}
                </select>
              ) : (
                <div style={{
                  padding: '9px 12px', border: '1px solid var(--border)', borderRadius: 8,
                  fontSize: 13, background: 'var(--bg-hover)', color: 'var(--text-secondary)',
                }}>
                  🏢 {nsi.step_name}
                </div>
              )}
            </div>
          );
        })()}

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 4 }}>
          <Btn variant="outline" disabled={modal.loading} onClick={onClose}>Cancel</Btn>
          <Btn
            onClick={() => onConfirm(modal)}
            loading={modal.loading}
            disabled={modal.loading || !canSubmit}
            style={{ background: btnColor, color: '#fff', border: 'none' }}
          >
            {btnLabel}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}

// ─── Icons ────────────────────────────────────────────────────────────────────
const ic = { width: 28, height: 28, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round', strokeLinejoin: 'round' };
const IcoLoad     = () => <svg {...ic}><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="12" y2="17"/></svg>;
const IcoReviewed = () => <svg {...ic}><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>;
const IcoPending  = () => <svg {...ic}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>;
const IcoReturn   = () => <svg {...ic}><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.51"/></svg>;
const IcoReject   = () => <svg {...ic}><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>;

// ─── Workspace definitions ────────────────────────────────────────────────────
const WORKSPACES = [
  {
    id: 'load_review',
    ws: 'WORKSPACE 1',
    label: 'LOAD & REVIEW',
    desc: 'Load Material Request records awaiting Cost Control review. Review lines and forward.',
    statuses: ['submitted', 'awaiting_cc_review', 'under_cc_review'],
    accent: '#1e3a8a',
    bg: '#eff6ff',
    Icon: IcoLoad,
  },
  {
    id: 'reviewed',
    ws: 'WORKSPACE 2',
    label: 'REVIEWED REQUESTS',
    desc: 'All requests successfully reviewed by Cost Control and forwarded onward. View only.',
    statuses: ['cc_reviewed'],
    accent: '#16a34a',
    bg: '#f0fdf4',
    Icon: IcoReviewed,
  },
  {
    id: 'pending_consideration',
    ws: 'WORKSPACE 3',
    label: 'PENDING CONSIDERATION',
    desc: 'Partially reviewed requests still being worked through by Cost Control.',
    statuses: ['partially_reviewed'],
    accent: '#d97706',
    bg: '#fffbeb',
    Icon: IcoPending,
  },
  {
    id: 'returned',
    ws: 'WORKSPACE 4',
    label: 'RETURNED REQUESTS',
    desc: 'Requests returned to the requester for corrections by Cost Control.',
    statuses: ['returned'],
    stage: 'cc_review',
    accent: '#b45309',
    bg: '#fef3c7',
    Icon: IcoReturn,
  },
  {
    id: 'rejected',
    ws: 'WORKSPACE 5',
    label: 'REJECTED REQUESTS',
    desc: 'Requests rejected outright by Cost Control. View only.',
    statuses: ['rejected'],
    stage: 'cc_review',
    accent: '#dc2626',
    bg: '#fef2f2',
    Icon: IcoReject,
  },
];

// ─── Workspace card ───────────────────────────────────────────────────────────
function WorkspaceCard({ ws, active, count, onClick }) {
  const [hovered, setHovered] = React.useState(false);
  const { ws: wsLabel, label, desc, accent, bg, Icon } = ws;

  const isLit = active || hovered;

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        flex: 1,
        minWidth: 0,
        background: (hovered || active) ? bg : '#fff',
        border: active
          ? `3px solid ${accent}`
          : hovered
            ? `2.5px solid ${accent}95`
            : `1.5px solid ${accent}70`,
        borderRadius: 12,
        padding: '16px 18px',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        boxShadow: isLit ? `0 6px 20px ${accent}22` : '0 1px 3px rgba(0,0,0,.05)',
        transform: hovered && !active ? 'translateY(-2px)' : 'none',
        transition: 'all .2s ease',
      }}
    >
      {/* Top row: workspace label + count badge */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{
          fontSize: 9.5, fontWeight: 800, letterSpacing: '0.12em',
          color: active ? accent : '#9ca3af', textTransform: 'uppercase',
        }}>
          {wsLabel}
        </span>
        <div style={{
          minWidth: 24, height: 24, borderRadius: 50, paddingInline: 6,
          background: accent,
          color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 11, fontWeight: 800,
          boxShadow: `0 2px 6px ${accent}55`,
        }}>
          {count ?? 0}
        </div>
      </div>

      {/* Icon */}
      <div style={{ color: accent, marginTop: 2 }}>
        <Icon />
      </div>

      {/* Title */}
      <div style={{
        fontSize: 12.5, fontWeight: 800, color: accent,
        textTransform: 'uppercase', letterSpacing: '0.04em', lineHeight: 1.3,
      }}>
        {label}
      </div>

      {/* Description */}
      <div style={{ fontSize: 11.5, color: '#6b7280', lineHeight: 1.5 }}>
        {desc}
      </div>
    </div>
  );
}

// ─── Section header bar ───────────────────────────────────────────────────────
function SectionBar({ accent, label, desc }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
      <div style={{ width: 4, height: 18, borderRadius: 4, background: accent }} />
      <span style={{ fontSize: 13, fontWeight: 700, color: accent, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </span>
      {desc && <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>— {desc}</span>}
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function MRCCQueue({ onOpen }) {
  const [stats, setStats]               = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [activeWs, setActiveWs]         = useState(null);

  const [rows, setRows]                 = useState([]);
  const [listLoading, setListLoading]   = useState(false);

  const [viewRow, setViewRow]           = useState(null);
  const [editRow, setEditRow]           = useState(null);
  const [actionModal, setActionModal]   = useState(null); // { row, actionType, comment, decisions, unreviewedCount, nextStepInfo, nextApproverId, loading }
  const [preparingForward, setPreparingForward] = useState(null); // request_id being prepared
  const [toast, setToast]               = useState(null);

  useEffect(() => {
    setStatsLoading(true);
    api.getMRCCStats()
      .then(setStats)
      .catch(() => setStats({}))
      .finally(() => setStatsLoading(false));
  }, []);

  const loadList = useCallback((ws) => {
    setListLoading(true);
    setRows([]);
    const params = { statuses: ws.statuses.join(','), limit: 100 };
    if (ws.stage) params.stage = ws.stage;
    api.getMRCCQueue(params)
      .then(r => setRows(r?.items || []))
      .catch(() => setRows([]))
      .finally(() => setListLoading(false));
  }, []);

  const handleSelect = (ws) => {
    setActiveWs(ws);
    setRows([]);
    loadList(ws);
  };

  const openAction = async (row, actionType, decisions = [], unreviewedCount = 0) => {
    if (actionType === 'forward') {
      // Fetch next-step details before opening — modal waits until data is ready
      if (!row.workflow_instance_id) {
        setToast({ msg: 'No workflow instance found for this request.', ok: false });
        setTimeout(() => setToast(null), 4000);
        return;
      }
      setPreparingForward(row.request_id);
      let nextStepInfo = null;
      let nextApproverId = '';
      try {
        const res = await api.getNextStepDetails(row.workflow_instance_id);
        nextStepInfo = res?.data || res;
        if (nextStepInfo?.approver_mode === 'specific_user' && nextStepInfo?.eligible_users?.length > 0) {
          nextApproverId = nextStepInfo.eligible_users[0].user_id || nextStepInfo.eligible_users[0].id;
        }
      } catch (_) {
        // Non-fatal — open modal anyway, step info just won't show
      } finally {
        setPreparingForward(null);
      }
      setActionModal({ row, actionType, comment: '', decisions, unreviewedCount, nextStepInfo, nextApproverId, loading: false });
    } else {
      setActionModal({ row, actionType, comment: '', decisions, unreviewedCount, nextStepInfo: null, nextApproverId: '', loading: false });
    }
  };

  const updateModal = (patch) => setActionModal(m => m ? { ...m, ...patch } : m);

  const handleActionConfirm = async (modal) => {
    const { row, actionType, comment, decisions = [], nextStepInfo, nextApproverId } = modal;
    if (!actionType || !(row?.request_id)) return;
    if ((actionType === 'reject' || actionType === 'return') && !(comment || '').trim()) return;

    setActionModal(m => ({ ...m, loading: true }));
    try {
      // Batch-save all line decisions, auto-marking un-reviewed lines
      const autoStatus = actionType === 'forward' ? 'approved' : 'rejected';
      await Promise.allSettled(
        decisions.map(d =>
          api.mrCCReviewLine(d.line_item_id, {
            cc_line_status: d.localStatus || autoStatus,
            cc_approved: (d.localStatus || autoStatus) === 'approved',
            cc_flag_potential_variation: d.localFlag || false,
          })
        )
      );

      // Then fire the workflow action
      if (actionType === 'forward') {
        await api.completeMRCCReview(row.request_id, {
          comment: comment || undefined,
          next_approver_id: nextStepInfo?.approver_mode === 'specific_user' && nextApproverId ? nextApproverId : undefined,
        });
      } else {
        if (!row.workflow_instance_id) throw new Error('No workflow instance found for this request.');
        await api.actOnWorkflow(row.workflow_instance_id, {
          action: actionType === 'return' ? 'return' : 'reject',
          comment,
        });
      }

      setActionModal(null);
      const stepLabel = nextStepInfo?.step_name ? `Forwarded to ${nextStepInfo.step_name}.` : 'Forwarded to next stage.';
      const msg = actionType === 'forward' ? stepLabel : actionType === 'return' ? 'Returned to requester.' : 'Request rejected.';
      setToast({ msg, ok: true });
      setTimeout(() => setToast(null), 3500);
      if (activeWs) loadList(activeWs);
      api.getMRCCStats().then(setStats).catch(() => {});
    } catch (err) {
      setActionModal(m => ({ ...m, loading: false }));
      setToast({ msg: err?.message || 'Action failed.', ok: false });
      setTimeout(() => setToast(null), 4000);
    }
  };

  const s = stats || {};

  if (statsLoading) {
    return <RNLLoader title="Loading workspaces..." subtitle="Fetching CC stats" />;
  }

  if (viewRow) {
    return <MRDetailsView row={viewRow} onBack={() => setViewRow(null)} />;
  }
  if (editRow) {
    return <MREditPage
      row={editRow}
      onBack={() => setEditRow(null)}
      onSaved={() => {
        setEditRow(null);
        if (activeWs) loadList(activeWs);
      }}
    />;
  }

  const isActionable = activeWs?.id === 'load_review';

  return (
    <div>
      {/* Action modal */}
      <ActionModal
        modal={actionModal}
        onClose={() => setActionModal(null)}
        onConfirm={handleActionConfirm}
        onUpdateModal={updateModal}
      />

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 9100,
          background: toast.ok ? '#16a34a' : '#ef4444',
          color: '#fff', padding: '12px 20px', borderRadius: 10,
          fontSize: 13, fontWeight: 600, boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
        }}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ margin: '0 0 4px', fontWeight: 700, fontSize: 18, color: 'var(--text-primary)' }}>
          Cost Control Review
        </h2>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>
          Vet Engineering material requirements against approved BOQ quantity, unit rate, budget value,
          cumulative transfers and variation exposure.
        </p>
      </div>

      {/* Workspace cards */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        {WORKSPACES.map(ws => (
          <WorkspaceCard
            key={ws.id}
            ws={ws}
            count={s[ws.id]}
            active={activeWs?.id === ws.id}
            onClick={() => handleSelect(ws)}
          />
        ))}
      </div>

      {/* Content area */}
      <div style={{ minHeight: 220, padding: '8px 0' }}>
        {!activeWs ? (
          <div style={{
            height: 160, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            gap: 8, color: '#9ca3af',
          }}>
            <span style={{ fontSize: 22 }}>👆</span>
            <span style={{ fontSize: 13, fontWeight: 500 }}>Select a workspace above to load its requests.</span>
          </div>
        ) : listLoading ? (
          <RNLLoader title={`Loading ${activeWs.label.toLowerCase()}...`} subtitle="Fetching requests" />
        ) : (
          <>
            <SectionBar accent={activeWs.accent} label={activeWs.label} desc={activeWs.desc} />
            <MRRequestTable
              rows={rows}
              loading={false}
              onOpen={(id) => setEditRow(rows.find(r => r.request_id === id))}
              onView={(row) => setViewRow(row)}
              onForward={isActionable ? (row, decisions, unreviewedCount) => openAction(row, 'forward', decisions, unreviewedCount) : undefined}
              onReturn={isActionable ? (row, decisions, unreviewedCount) => openAction(row, 'return', decisions, unreviewedCount) : undefined}
              onReject={isActionable ? (row, decisions, unreviewedCount) => openAction(row, 'reject', decisions, unreviewedCount) : undefined}
              preparingForward={preparingForward}
              isCC={isActionable}
              emptyMsg={`No requests in "${activeWs.label}".`}
            />
          </>
        )}
      </div>
    </div>
  );
}
